/**
 * Faturas mensais (Adendo C) e notas fiscais ANEXADAS — Fase 5.
 *
 * ---------------------------------------------------------------------------
 * NÃO EXISTE "CRIAR FATURA" DE PROPÓSITO
 * ---------------------------------------------------------------------------
 * A fatura NASCE da acumulação das reservas fechadas do cliente FATURADO_MENSAL
 * — a API não tem `POST /invoices`, e esta camada tampouco. O painel fecha,
 * dá baixa, cancela (com motivo) e anexa a nota fiscal emitida FORA
 * (decisão nº 4 do ROADMAP: anexar, não emitir).
 *
 * O XML sobe pelo caminho assinado de todo anexo do repo: `POST` assina a URL,
 * o `PUT` vai direto no storage (o binário nunca toca a API) e a CHAVE
 * devolvida é o que `POST /fiscal-notes` vincula.
 */

import { STATUS_DE_FATURA, type StatusDeFatura } from '@locaveri/shared'
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import { paginaDe } from '@/features/precos/api'
import { apiGet, apiPost } from '@/lib/api'
import { ROTAS_DE_ARQUIVOS, ROTAS_DE_FATURAS } from '@/lib/endpoints'
import { montarQuery } from '@/lib/query-string'

// ---------------------------------------------------------------------------
// Modelo de leitura (espelha `faturas.dto.ts` da API)
// ---------------------------------------------------------------------------

export const itemDeFaturaSchema = z.object({
  id: z.uuid(),
  /** `null` é ajuste manual — o campo antecipa o dado, não a feature. */
  reservationId: z.uuid().nullable(),
  descricao: z.string(),
  /** Centavos como string decimal (o wire de `Money`). */
  valor: z.string(),
  criadoEm: z.string(),
})

export type ItemDeFatura = z.infer<typeof itemDeFaturaSchema>

export const notaFiscalSchema = z.object({
  id: z.uuid(),
  /** 44 dígitos, DV conferido na entrada. */
  chaveDeAcesso: z.string(),
  /** O XML no storage — a leitura assinada sai de `POST /files/read-url`. */
  arquivoChave: z.string(),
  valor: z.string(),
  reservationId: z.uuid().nullable(),
  invoiceId: z.uuid().nullable(),
  criadoEm: z.string(),
})

export type NotaFiscal = z.infer<typeof notaFiscalSchema>

export const faturaSchema = z.object({
  id: z.uuid(),
  accountId: z.uuid(),
  /** Competência: mês-calendário em America/Sao_Paulo. */
  ano: z.number().int(),
  mes: z.number().int(),
  status: z.enum(STATUS_DE_FATURA),
  /** Soma dos itens. Travado quando a fatura FECHA. */
  total: z.string(),
  fechadaEm: z.string().nullable(),
  venceEm: z.string().nullable(),
  pagaEm: z.string().nullable(),
  canceladaEm: z.string().nullable(),
  observacoes: z.string().nullable(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type Fatura = z.infer<typeof faturaSchema>

export const faturaDetalhadaSchema = faturaSchema.extend({
  itens: z.array(itemDeFaturaSchema),
  notasFiscais: z.array(notaFiscalSchema),
  /** Ids das transações que a quitaram — a transação inteira é de `/payments`. */
  transacaoIds: z.array(z.uuid()),
})

export type FaturaDetalhada = z.infer<typeof faturaDetalhadaSchema>

const paginaDeFaturasSchema = paginaDe(faturaSchema)

export type PaginaDeFaturas = z.infer<typeof paginaDeFaturasSchema>

const listaDeNotasSchema = z.object({ itens: z.array(notaFiscalSchema) })

// ---------------------------------------------------------------------------
// Apresentação
// ---------------------------------------------------------------------------

/** `mes=3, ano=2026` → `03/2026`. Competência não é data — não passa por `Date`. */
export function rotuloDaCompetencia(mes: number, ano: number): string {
  return `${String(mes).padStart(2, '0')}/${String(ano)}`
}

// ---------------------------------------------------------------------------
// Filtros e chaves
// ---------------------------------------------------------------------------

export interface FiltrosDeFaturas {
  readonly pagina: number
  readonly tamanho: number
  readonly status: StatusDeFatura | ''
  readonly accountId: string
}

export const CHAVE_DE_FATURAS = 'faturas'

export const chavesDeFaturas = {
  lista: (filtros: FiltrosDeFaturas) => [CHAVE_DE_FATURAS, 'lista', filtros] as const,
  detalhe: (id: string) => [CHAVE_DE_FATURAS, 'detalhe', id] as const,
  notasDaReserva: (reservaId: string) => [CHAVE_DE_FATURAS, 'notas-da-reserva', reservaId] as const,
}

function invalidarFaturas(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_FATURAS] })
}

// ---------------------------------------------------------------------------
// Leitura
// ---------------------------------------------------------------------------

export function useFaturas(filtros: FiltrosDeFaturas): UseQueryResult<PaginaDeFaturas> {
  return useQuery({
    queryKey: chavesDeFaturas.lista(filtros),
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        status: filtros.status,
        accountId: filtros.accountId,
      })
      return apiGet(`${ROTAS_DE_FATURAS.lista}${query}`, paginaDeFaturasSchema, { signal })
    },
    placeholderData: (anterior) => anterior,
  })
}

export function useFatura(id: string | null): UseQueryResult<FaturaDetalhada> {
  return useQuery({
    queryKey: chavesDeFaturas.detalhe(id ?? 'nenhuma'),
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_FATURAS.porId(id ?? ''), faturaDetalhadaSchema, { signal }),
    enabled: id !== null,
  })
}

export function useNotasDaReserva(
  reservaId: string | null,
): UseQueryResult<{ itens: NotaFiscal[] }> {
  return useQuery({
    queryKey: chavesDeFaturas.notasDaReserva(reservaId ?? 'nenhuma'),
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_FATURAS.notasDaReserva(reservaId ?? ''), listaDeNotasSchema, { signal }),
    enabled: reservaId !== null,
  })
}

// ---------------------------------------------------------------------------
// Escrita
// ---------------------------------------------------------------------------

export function useFecharFatura(): UseMutationResult<Fatura, Error, { readonly id: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id }) => apiPost(ROTAS_DE_FATURAS.fechar(id), faturaSchema, {}),
    onSuccess: () => {
      invalidarFaturas(queryClient)
    },
  })
}

export function useMarcarFaturaPaga(): UseMutationResult<Fatura, Error, { readonly id: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id }) => apiPost(ROTAS_DE_FATURAS.marcarPaga(id), faturaSchema, {}),
    onSuccess: () => {
      invalidarFaturas(queryClient)
    },
  })
}

export function useCancelarFatura(): UseMutationResult<
  Fatura,
  Error,
  { readonly id: string; readonly motivo: string }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, motivo }) =>
      apiPost(ROTAS_DE_FATURAS.cancelar(id), faturaSchema, { motivo }),
    onSuccess: () => {
      invalidarFaturas(queryClient)
    },
  })
}

const uploadAssinadoSchema = z.object({
  urlDeUpload: z.string(),
  cabecalhos: z.record(z.string(), z.string()),
  chave: z.string(),
})

export interface AnexoDeNotaFiscal {
  /** O XML da NF-e, direto do sistema emissor. */
  readonly arquivo: File
  /** 44 dígitos (a validação com DV acontece no dialog, pelo schema do shared). */
  readonly chaveDeAcesso: string
  /** Centavos como string — precisa bater com o total do alvo. */
  readonly valor: string
  /** Exatamente UM dos dois. */
  readonly reservationId?: string
  readonly invoiceId?: string
}

/**
 * Anexa a nota: assina o upload do XML, faz o `PUT` cru no storage (cabeçalhos
 * EXATAMENTE os assinados) e vincula a chave em `POST /fiscal-notes`.
 */
export function useAnexarNotaFiscal(): UseMutationResult<NotaFiscal, Error, AnexoDeNotaFiscal> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ arquivo, chaveDeAcesso, valor, reservationId, invoiceId }) => {
      // Alguns sistemas soltam o XML sem MIME — o contrato só aceita XML.
      const tipoMime = arquivo.type === '' ? 'application/xml' : arquivo.type
      const assinatura = await apiPost(ROTAS_DE_ARQUIVOS.xmlDeNotaFiscal, uploadAssinadoSchema, {
        tipoMime,
        tamanhoEmBytes: arquivo.size,
      })
      const resposta = await fetch(assinatura.urlDeUpload, {
        method: 'PUT',
        headers: assinatura.cabecalhos,
        body: arquivo,
      }).catch(() => null)
      if (!resposta?.ok) {
        throw new Error('O armazenamento recusou o XML. Tente de novo.')
      }
      return apiPost(ROTAS_DE_FATURAS.anexarNota, notaFiscalSchema, {
        chaveDeAcesso,
        arquivoChave: assinatura.chave,
        valor,
        ...(reservationId === undefined ? {} : { reservationId }),
        ...(invoiceId === undefined ? {} : { invoiceId }),
      })
    },
    onSuccess: () => {
      invalidarFaturas(queryClient)
    },
  })
}
